import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bell,
  Check,
  ChevronRight,
  Coins,
  Crown,
  FileText,
  History,
  MessageCircle,
  Play,
  RefreshCw,
  Rocket,
  type LucideIcon,
} from 'lucide-react';
import {
  EMPTY_FILTER_BODY,
  EMPTY_FILTER_TITLE,
  EMPTY_INBOX_BODY,
  EMPTY_INBOX_TITLE,
  INBOX_FILTERS,
  KIND_META,
  SAMPLE_INBOX,
  VIDEO_NOTE,
  cardLabel,
  cloneInbox,
  filterCount,
  filterInbox,
  inboxLabel,
  unreadCount,
  unreadLabel,
  type InboxFilter,
  type InboxItem,
  type InboxKind,
} from '../../data/notifications-mock';
import { ArticleThumb, InboxArt, VideoThumb } from './NotificationArt';
import './notifications.css';

const PREVIEW_MS = 680;

const ICONS: Record<InboxKind, LucideIcon> = {
  video: Play,
  article: FileText,
  mock_coins: Coins,
  support_reply: MessageCircle,
  ticket_closed: Check,
  app_update: Rocket,
  account_sync: RefreshCw,
  data_restore: History,
  premium: Crown,
  general: Bell,
};

export interface NotificationsViewProps {
  /**
   * Leave unset to preview skeletons once.
   * Pass a real flag once the inbox API is wired.
   */
  loading?: boolean;
  /**
   * Leave unset to use the built-in sample.
   * Pass an array from the API. An empty array shows the empty illustration.
   * Keep the array reference stable, or each new array replaces local read state.
   */
  items?: InboxItem[];
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), PREVIEW_MS);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`nt-skel ${className}`} />;
}

function SummarySkeleton() {
  return (
    <div className="nt-summary" data-testid="skeleton-summary" aria-hidden="true">
      <div className="nt-skel__stack">
        <Skel className="nt-skel--title" />
        <Skel className="nt-skel--line" />
      </div>
      <Skel className="nt-skel--btn" />
    </div>
  );
}

function FiltersSkeleton() {
  return (
    <div className="nt-filters" data-testid="skeleton-filters" aria-hidden="true">
      {[0, 1, 2, 3].map((key) => (
        <Skel key={key} className="nt-skel--chip" />
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="nt-list" data-testid="skeleton-list" aria-hidden="true">
      {[0, 1, 2].map((key) => (
        <Skel key={key} className="nt-skel--card" />
      ))}
    </div>
  );
}

function CardBody({ item }: { item: InboxItem }) {
  const meta = KIND_META[item.kind];
  const Icon = ICONS[item.kind];
  return (
    <>
      <span className="nt-card__main">
        <span className="nt-ico">
          <Icon size={20} aria-hidden="true" />
        </span>
        <span className="nt-card__body">
          <span className="nt-card__meta">
            <span className="nt-badge">{meta.label}</span>
            <span className="nt-time">{item.time}</span>
          </span>
          <span className="nt-card__title">{item.title}</span>
          <span className="nt-card__text">{item.body}</span>
        </span>
        <ChevronRight className="nt-chevron" size={18} aria-hidden="true" />
      </span>
      {item.kind === 'video' && (
        <span className="nt-thumb">
          <VideoThumb />
          <span className="nt-thumb__play">
            <Play size={18} aria-hidden="true" />
          </span>
        </span>
      )}
      {item.kind === 'article' && (
        <span className="nt-thumb">
          <ArticleThumb />
        </span>
      )}
      <span className="nt-hint">{meta.hint}</span>
    </>
  );
}

const NotificationsView: React.FC<NotificationsViewProps> = ({ loading: loadingProp, items: itemsProp }) => {
  const autoLoading = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? autoLoading;
  const titleId = useId();
  const [filter, setFilter] = useState<InboxFilter>('all');
  const [rows, setRows] = useState<InboxItem[]>(() => cloneInbox(itemsProp ?? SAMPLE_INBOX));
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    setRows(cloneInbox(itemsProp ?? SAMPLE_INBOX));
    setOpenId(null);
  }, [itemsProp]);

  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenId(null);
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [openId]);

  const markRead = (id: string) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, unread: false } : row)));
  };

  const markAllRead = () => {
    setRows((current) => current.map((row) => ({ ...row, unread: false })));
  };

  const visible = filterInbox(rows, filter);
  const unread = unreadCount(rows);
  const open = rows.find((row) => row.id === openId) ?? null;
  const openMeta = open ? KIND_META[open.kind] : null;
  const OpenIcon = open ? ICONS[open.kind] : Bell;
  const inboxEmpty = rows.length === 0;

  return (
    <section className="nt" aria-busy={loading || undefined}>
      {loading ? (
        <>
          <p className="visually-hidden" role="status">
            Loading notifications
          </p>
          <SummarySkeleton />
          <FiltersSkeleton />
          <ListSkeleton />
        </>
      ) : (
        <>
          <header className="nt-summary">
            <div>
              <h2>{unreadLabel(unread)}</h2>
              <p>{inboxLabel(rows.length)}</p>
            </div>
            <button type="button" className="nt-mark" onClick={markAllRead} disabled={unread === 0}>
              Mark all read
            </button>
          </header>

          <div className="nt-filters" role="group" aria-label="Filter notifications">
            {INBOX_FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                className="nt-chip"
                aria-pressed={filter === item.id}
                onClick={() => setFilter(item.id)}
              >
                {item.label}
                <span>{filterCount(rows, item.id)}</span>
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <div
              className="nt-empty"
              data-testid={inboxEmpty ? 'notifications-empty' : 'notifications-filter-empty'}
            >
              <InboxArt />
              <h2>{inboxEmpty ? EMPTY_INBOX_TITLE : EMPTY_FILTER_TITLE}</h2>
              <p>{inboxEmpty ? EMPTY_INBOX_BODY : EMPTY_FILTER_BODY}</p>
              {!inboxEmpty && (
                <button type="button" className="nt-mark" onClick={() => setFilter('all')}>
                  Show all
                </button>
              )}
            </div>
          ) : (
            <ul className="nt-list">
              {visible.map((item) => {
                const meta = KIND_META[item.kind];
                const className = `nt-card${item.unread ? ' is-unread' : ''}`;
                const label = cardLabel(item);
                if (meta.action === 'link' && item.href) {
                  return (
                    <li key={item.id}>
                      <Link
                        className={className}
                        data-tone={item.kind}
                        to={item.href}
                        aria-label={label}
                        onClick={() => markRead(item.id)}
                      >
                        <CardBody item={item} />
                      </Link>
                    </li>
                  );
                }
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={className}
                      data-tone={item.kind}
                      aria-label={label}
                      onClick={() => {
                        markRead(item.id);
                        setOpenId(item.id);
                      }}
                    >
                      <CardBody item={item} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {open && openMeta && (
        <div className="nt-modal" onMouseDown={() => setOpenId(null)}>
          <div
            className="nt-dialog"
            data-tone={open.kind}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <span className="nt-dialog__handle" aria-hidden="true" />
            <p className="nt-badge">{openMeta.label}</p>
            <div className="nt-dialog__hero">
              <OpenIcon size={28} aria-hidden="true" />
            </div>
            {open.kind === 'video' && (
              <div className="nt-dialog__art">
                <VideoThumb />
                <span className="nt-thumb__play">
                  <Play size={18} aria-hidden="true" />
                </span>
              </div>
            )}
            <h2 id={titleId}>{open.title}</h2>
            {open.detail && <p className="nt-detail">{open.detail}</p>}
            <p>{open.body}</p>
            {open.kind === 'video' && <p>{VIDEO_NOTE}</p>}
            <div className="nt-dialog__actions">
              {open.kind === 'video' && (
                <Link className="nt-ghost" to="/app/articles" onClick={() => setOpenId(null)}>
                  Read articles
                </Link>
              )}
              <button type="button" className="nt-primary" onClick={() => setOpenId(null)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default NotificationsView;
